/**
 * /tarifs/calculateur — questionnaire that recommends a plan.
 *
 * All limits, prices and packs come from the public offer through
 * src/lib/plan-finder.ts (which reads marketing-plan-matrix.ts and
 * currency.ts). The page never states a number that is not already on
 * /tarifs or /tarifs/comparatif.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { PLAN_DISPLAY_NAMES, PLAN_AUDIENCE, locText } from "@/lib/marketing-plan-matrix";
import { useLocale, intlLocale } from "@/lib/locale";
import { formatMoney } from "@/lib/currency";
import { cn } from "@/lib/utils";
import {
  CREDITS_PER_ARTICLE,
  recommendPlan,
  type ConstraintKey,
  type NeedKey,
  type PlanFinderInput,
  type PlanOption,
  type QualityTier,
} from "@/lib/plan-finder";

const ARTICLE_STEPS = [10, 25, 50, 100, 150, 200, 300, 500, 750, 1000, 1500, 2000, 3000, 5000, 8000, 12000];
const SITE_OPTIONS = [1, 2, 3, 5, 10, 25, 50];
const LANGUAGE_OPTIONS = [1, 2, 3, 5, 10, 15];
const SEAT_OPTIONS = [1, 2, 5, 15, 50, 100];
const QUALITIES: QualityTier[] = ["standard", "advanced", "premium"];
const NEEDS: NeedKey[] = ["hourly", "realtime", "qa", "api_write", "webhooks", "white_label", "ads", "programmatic", "priority_support"];

function OptionRow<T extends number>({
  id,
  options,
  value,
  onChange,
  format,
}: {
  id: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
  format: (v: T) => string;
}) {
  return (
    <div role="radiogroup" aria-labelledby={`${id}-label`} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={value === o}
          onClick={() => onChange(o)}
          className={cn(
            "min-w-12 border px-3 py-2 text-sm tabular-nums transition-colors",
            value === o ? "border-foreground bg-foreground text-background" : "border-rule bg-background hover:border-foreground",
          )}
        >
          {format(o)}
        </button>
      ))}
    </div>
  );
}

export default function PlanFinderPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const nf = useMemo(() => new Intl.NumberFormat(intlLocale(locale)), [locale]);

  const [articleIdx, setArticleIdx] = useState(3); // 100 articles
  const [sites, setSites] = useState(1);
  const [languages, setLanguages] = useState(1);
  const [seats, setSeats] = useState(2);
  const [quality, setQuality] = useState<QualityTier>("standard");
  const [needs, setNeeds] = useState<NeedKey[]>([]);
  const [annual, setAnnual] = useState(false);

  const input: PlanFinderInput = useMemo(
    () => ({ articlesPerMonth: ARTICLE_STEPS[articleIdx], sites, languages, seats, quality, needs, annual }),
    [articleIdx, sites, languages, seats, quality, needs, annual],
  );
  const result = useMemo(() => recommendPlan(input), [input]);

  const toggleNeed = (k: NeedKey, on: boolean) =>
    setNeeds((prev) => (on ? Array.from(new Set([...prev, k])) : prev.filter((x) => x !== k)));

  const k = (key: string, opts?: Record<string, unknown>) => t(`marketing.pages.planFinder.${key}`, opts);
  const plural = (n: number) => (n >= 2 ? "many" : "one");

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.header.nav.pricing"), path: "/tarifs" },
      { name: k("seo.title"), path: "/tarifs/calculateur" },
    ]),
  ];

  const reasonLabel = (c: ConstraintKey) => k(`reasons.${c}`);

  const renderOption = (opt: PlanOption, recommended: boolean) => {
    const name = locText(PLAN_DISPLAY_NAMES[opt.slug], locale);
    const perArticle = input.articlesPerMonth > 0 ? Math.round(opt.totalMonthlyCents / input.articlesPerMonth) : 0;
    return (
      <article
        className={cn("flex flex-col border bg-background p-6", recommended ? "border-foreground ring-2 ring-accent-press" : "border-rule")}
        aria-live={recommended ? "polite" : undefined}
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {recommended ? k("result.recommended") : k("result.alternative")}
            </p>
            <h3 className="mt-1 text-2xl font-medium tracking-tight">{name}</h3>
            <p className="text-sm text-muted-foreground">{locText(PLAN_AUDIENCE[opt.slug], locale)}</p>
          </div>
          {recommended && <Badge>{k("result.bestValue")}</Badge>}
        </header>

        <div className="mt-6">
          <div className="text-4xl font-medium tabular-nums tracking-tight">{formatMoney(opt.planCents, locale)}</div>
          <p className="mt-1 text-xs text-muted-foreground">{annual ? k("result.perMonthAnnual") : k("result.perMonth")}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {annual ? k("result.setupWaived") : k("result.setup", { amount: formatMoney(opt.setupCents, locale) })}
          </p>
        </div>

        <ul className="mt-6 space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
            <span>{k("result.credits", { included: nf.format(opt.includedCredits), needed: nf.format(opt.neededCredits) })}</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
            <span>{k("result.articlesCovered", { n: nf.format(opt.includedArticles) })}</span>
          </li>
          {opt.shortfall > 0 && (
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
              <span>
                {k("result.topup", {
                  packs: opt.packs.map((p) => `${p.count} × ${nf.format(p.pack.credits)}`).join(" + "),
                  amount: formatMoney(opt.packCents, locale),
                })}
              </span>
            </li>
          )}
        </ul>

        <div className="mt-6 border-t border-rule pt-4">
          <p className="text-sm">
            <span className="font-medium">{k("result.total", { amount: formatMoney(opt.totalMonthlyCents, locale) })}</span>
            {input.articlesPerMonth > 0 && (
              <span className="text-muted-foreground"> · {k("result.perArticle", { amount: formatMoney(perArticle, locale, { fraction: true }) })}</span>
            )}
          </p>
        </div>

        <Button asChild className="mt-6 w-full" variant={recommended ? "default" : "outline"}>
          <Link to={`/dpadmin?intent=signup&plan=${opt.slug}${annual ? "&billing=annual" : ""}`}>
            {recommended && <Sparkles className="mr-2 h-4 w-4" aria-hidden />}
            {t("marketing.pages.tarifs.plans.ctaSelect", { plan: name })}
          </Link>
        </Button>
      </article>
    );
  };

  return (
    <>
      <SeoHead title={k("seo.title")} description={k("seo.description")} path="/tarifs/calculateur" locale={locale} jsonLd={jsonLd} />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{k("hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">{k("hero.title")}</DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{k("hero.lead")}</p>
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          {/* Questionnaire */}
          <form className="space-y-10 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
            <fieldset>
              <Label id="pf-articles-label" className="text-base font-medium">{k("q.articles.label")}</Label>
              <p className="mt-1 text-sm text-muted-foreground">{k("q.articles.hint")}</p>
              <div className="mt-4 flex items-center gap-4">
                <Slider
                  aria-labelledby="pf-articles-label"
                  min={0}
                  max={ARTICLE_STEPS.length - 1}
                  step={1}
                  value={[articleIdx]}
                  onValueChange={(v) => setArticleIdx(v[0])}
                  className="flex-1"
                />
                <span className="w-24 text-right text-2xl font-medium tabular-nums">{nf.format(ARTICLE_STEPS[articleIdx])}</span>
              </div>
            </fieldset>

            <fieldset>
              <Label id="pf-sites-label" className="text-base font-medium">{k("q.sites.label")}</Label>
              <div className="mt-3">
                <OptionRow id="pf-sites" options={SITE_OPTIONS} value={sites} onChange={setSites} format={(n) => (n === 50 ? `${nf.format(25)}+` : nf.format(n))} />
              </div>
            </fieldset>

            <fieldset>
              <Label id="pf-languages-label" className="text-base font-medium">{k("q.languages.label")}</Label>
              <p className="mt-1 text-sm text-muted-foreground">{k("q.languages.hint")}</p>
              <div className="mt-3">
                <OptionRow id="pf-languages" options={LANGUAGE_OPTIONS} value={languages} onChange={setLanguages} format={(n) => (n === 15 ? `${nf.format(10)}+` : nf.format(n))} />
              </div>
            </fieldset>

            <fieldset>
              <Label id="pf-seats-label" className="text-base font-medium">{k("q.seats.label")}</Label>
              <div className="mt-3">
                <OptionRow id="pf-seats" options={SEAT_OPTIONS} value={seats} onChange={setSeats} format={(n) => (n === 100 ? `${nf.format(50)}+` : nf.format(n))} />
              </div>
            </fieldset>

            <fieldset>
              <Label id="pf-quality-label" className="text-base font-medium">{k("q.quality.label")}</Label>
              <div role="radiogroup" aria-labelledby="pf-quality-label" className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {QUALITIES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    role="radio"
                    aria-checked={quality === q}
                    onClick={() => setQuality(q)}
                    className={cn(
                      "border p-4 text-left transition-colors",
                      quality === q ? "border-foreground ring-1 ring-foreground" : "border-rule hover:border-foreground",
                    )}
                  >
                    <span className="block text-sm font-medium">{k(`q.quality.${q}.label`)}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">{k(`q.quality.${q}.hint`)}</span>
                    <span className="mt-2 block text-xs tabular-nums text-muted-foreground">
                      {k("q.quality.creditsPerArticle", { n: CREDITS_PER_ARTICLE[q] })}
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <Label className="text-base font-medium">{k("q.needs.label")}</Label>
              <p className="mt-1 text-sm text-muted-foreground">{k("q.needs.hint")}</p>
              <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {NEEDS.map((n) => (
                  <li key={n} className="flex items-start gap-3">
                    <Checkbox id={`pf-need-${n}`} className="mt-0.5" checked={needs.includes(n)} onCheckedChange={(v) => toggleNeed(n, v === true)} />
                    <div>
                      <Label htmlFor={`pf-need-${n}`} className="text-sm font-normal">{k(`q.needs.${n}`)}</Label>
                      <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{k(`q.needs.hints.${n}`)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </fieldset>

            <fieldset className="flex items-center gap-3">
              <Label htmlFor="pf-billing" className={!annual ? "font-medium" : "text-muted-foreground"}>{t("marketing.pages.tarifs.hero.billingMonthly")}</Label>
              <Switch id="pf-billing" checked={annual} onCheckedChange={setAnnual} aria-label={t("marketing.pages.tarifs.hero.billingAnnual")} />
              <Label htmlFor="pf-billing" className={annual ? "font-medium" : "text-muted-foreground"}>
                {t("marketing.pages.tarifs.hero.billingAnnual")}
                <Badge variant="default" className="ml-2 text-[10px]">{t("marketing.pages.tarifs.hero.annualBadge")}</Badge>
              </Label>
            </fieldset>
          </form>

          {/* Result */}
          <aside className="space-y-4 lg:col-span-2 lg:sticky lg:top-24 lg:self-start">
            <Eyebrow>{k("result.eyebrow")}</Eyebrow>

            {result.kind === "plan" && result.recommended ? (
              <>
                {renderOption(result.recommended, true)}
                {Object.keys(result.ruledOut).length > 0 && (
                  <div className="border border-rule p-4 text-sm">
                    <p className="font-medium">{k("result.whyTitle")}</p>
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      {(Object.entries(result.ruledOut) as [PlanOption["slug"], ConstraintKey[]][]).map(([slug, cs]) => (
                        <li key={slug}>
                          {k("result.whyLine", {
                            plan: locText(PLAN_DISPLAY_NAMES[slug], locale),
                            reasons: Array.from(new Set(cs)).map(reasonLabel).join(", "),
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.alternative && renderOption(result.alternative, false)}
              </>
            ) : (
              <article className="border border-dashed border-rule p-6">
                <h3 className="text-2xl font-medium tracking-tight">{t("marketing.pages.tarifs.plans.enterpriseName")}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {result.enterpriseReasons === "volume"
                    ? k("result.enterpriseVolume", { credits: nf.format(result.neededCredits) })
                    : k("result.enterpriseLimits", { reasons: (result.enterpriseReasons ?? []).map(reasonLabel).join(", ") })}
                </p>
                <Button asChild className="mt-6">
                  <Link to="/contact?subject=enterprise">
                    {t("marketing.pages.tarifs.plans.ctaEnterprise")}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </article>
            )}

            {input.articlesPerMonth <= 25 && (
              <p className="text-sm text-muted-foreground">{k("result.freeHint")}</p>
            )}

            <p className="text-xs text-muted-foreground">
              {k("result.footnote", { n: CREDITS_PER_ARTICLE[input.quality], count: plural(CREDITS_PER_ARTICLE[input.quality]) })}{" "}
              <Link to="/tarifs/comparatif" className="underline underline-offset-4">{k("result.compareLink")}</Link>
            </p>
          </aside>
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{k("method.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">{k("method.title")}</DisplayHeading>
          <ul className="mt-6 space-y-3 text-base leading-relaxed text-muted-foreground">
            {(k("method.items", { returnObjects: true }) as unknown as string[]).map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-foreground" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/tarifs">{k("method.pricingLink")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/tarifs/comparatif">{k("method.comparisonLink")}</Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
